import { type CreditPackage } from '@frontend/api-types';
import type { ReactNode } from '@frontend/react';
import { useCreditPackages } from '@frontend/react-domain';
import { Button, Divider, Text, XStack, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import { usePricingTerminology } from '@frontend/web/context';
import { useState } from 'react';
import { CreditPackagesList } from './CreditPackagesList';

export type UpgradeOffer = {
  commentary: string;
  onPress: () => void;
  ornament?: ReactNode;
  subtitle: string;
  title: string;
};

export type SelectCreditsProps = {
  minimumCredits?: number;
  onSelectCreditPackage: (pkg: CreditPackage) => void;
  selectedCreditPackage?: CreditPackage;
  upgradeOffer?: UpgradeOffer;
};

export const SelectCredits = ({
  minimumCredits,
  onSelectCreditPackage,
  selectedCreditPackage,
  upgradeOffer,
}: SelectCreditsProps) => {
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const packages = useCreditPackages({ pricingTerminology });
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | undefined>(selectedCreditPackage);
  return (
    <YStack className={safeCss('py-sm')} gap="sm">
      {upgradeOffer ? (
        <>
          <YStack className={safeCss('px-md pt-sm')}>
            <XStack
              alignItems="center"
              bg="white"
              borderColor="borderPrimary"
              borderWidth={2}
              className="cursor-pointer hover:border-borderSecondary"
              gapX="md"
              onClick={upgradeOffer.onPress}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  upgradeOffer.onPress();
                }
              }}
              p="md"
              radius="lg"
              role="button"
              tabIndex={0}
            >
              <YStack flex={1} gapY="xs">
                <XStack alignItems="center" gapX="sm">
                  <Text type="h4">{upgradeOffer.title}</Text>
                  {upgradeOffer.ornament}
                </XStack>
                <Text type="p-sm">{upgradeOffer.commentary}</Text>
              </YStack>
              <Text type="h4">{upgradeOffer.subtitle}</Text>
            </XStack>
          </YStack>
          <XStack alignItems="center" className={safeCss('px-md pt-sm')} gapX="md" justifyContent="between">
            <YStack bg="neutral200" flex={1} style={{ height: 1 }} />
            <Text type="p-sm">OR</Text>
            <YStack bg="neutral200" flex={1} style={{ height: 1 }} />
          </XStack>
        </>
      ) : null}
      <YStack className={safeCss('px-md')} gap="sm">
        {upgradeOffer ? (
          <Text color="secondary" type="p-sm">
            {showCredits
              ? `Pay as you go with ${pricingTerminology.unit_term} packs`
              : 'Pay as you go for additional tasks'}
          </Text>
        ) : null}
        <CreditPackagesList
          minimumCredits={minimumCredits}
          onSelectPackage={setSelectedPackage}
          packages={packages}
          selectedPackage={selectedPackage}
        />
      </YStack>
      <Divider />
      <XStack className="px-md py-sm">
        <Button
          disabled={!selectedPackage}
          fullWidth
          label="Continue"
          onClick={() => {
            if (!selectedPackage) {
              return;
            }
            onSelectCreditPackage(selectedPackage);
          }}
          variant="primary"
        />
      </XStack>
    </YStack>
  );
};
